import { lazy, Suspense, Component, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Loader, Center, Button, Stack, Text } from '@mantine/core';
import { AuthProvider, useAuth, isClientUser } from './auth/AuthContext.js';
import { ClientShell } from './components/client/ClientShell.js';
import { InternalShell } from './components/internal/InternalShell.js';
// Login и Home грузим сразу — это первый экран; остальное по требованию (code-splitting).
import { LoginPage } from './pages/Login.js';
import { HomePage } from './pages/Home.js';
import type { SimpleCatalogKind } from '@swiftpay/shared';
import { canSeePlanFact, canSeePnl } from './navigation/sections.js';

// Страховка от «битого» чанка после деплоя: если динамический import() не
// загрузился (старый index.html ссылается на удалённый с сервера чанк → 404),
// один раз перезагружаем страницу, чтобы взять свежий index.html и манифест.
// Флаг в sessionStorage защищает от бесконечного цикла перезагрузок.
const RELOAD_FLAG = 'chunk-reload-attempted';
function reloadOnChunkError<T>(p: Promise<T>): Promise<T> {
  return p.then(
    (v) => { sessionStorage.removeItem(RELOAD_FLAG); return v; },
    (err) => {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
      }
      throw err; // повторно упали (флаг уже стоял) — пробрасываем в Suspense/ErrorBoundary
    },
  );
}

// Последний рубеж: если чанк не загрузился даже после reload-попытки (или упала
// сама страница), показываем внятный экран с кнопкой вместо мёртвого белого.
class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Center h="100vh" p="md">
        <Stack align="center" gap="sm">
          <Text fw={600}>Не удалось загрузить страницу</Text>
          <Text size="sm" c="dimmed">Похоже, вышло обновление портала.</Text>
          <Button onClick={() => { sessionStorage.removeItem(RELOAD_FLAG); window.location.reload(); }}>
            Обновить
          </Button>
        </Stack>
      </Center>
    );
  }
}

// Хелпер: ленивый импорт именованного экспорта (страницы экспортируются по имени, не default).
// Тип берётся ровно от выбранного экспорта m[name], чтобы пропсы страницы сохранились.
function lazyNamed<M, K extends keyof M & string>(loader: () => Promise<M>, name: K): M[K] {
  // m[name] приводим к React.lazy-совместимому виду; наружу отдаём точный тип компонента M[K].
  return lazy(() => reloadOnChunkError(loader()).then((m) => ({ default: m[name] as React.ComponentType<unknown> }))) as unknown as M[K];
}

const ActivatePage = lazyNamed(() => import('./pages/Activate.js'), 'ActivatePage');
const ResetRequestPage = lazyNamed(() => import('./pages/ResetRequest.js'), 'ResetRequestPage');
const ResetConfirmPage = lazyNamed(() => import('./pages/ResetConfirm.js'), 'ResetConfirmPage');
const ConfirmEmailPage = lazyNamed(() => import('./pages/ConfirmEmail.js'), 'ConfirmEmailPage');
const ProducersPage = lazyNamed(() => import('./pages/admin/Producers.js'), 'ProducersPage');
const ProductGroupsPage = lazyNamed(() => import('./pages/admin/ProductGroups.js'), 'ProductGroupsPage');
const ProductsPage = lazyNamed(() => import('./pages/admin/Products.js'), 'ProductsPage');
const LegalEntitiesPage = lazyNamed(() => import('./pages/admin/LegalEntities.js'), 'LegalEntitiesPage');
const SimpleCatalogPage = lazyNamed(() => import('./pages/admin/SimpleCatalog.js'), 'SimpleCatalogPage');
const CommercialTermsPage = lazyNamed(() => import('./pages/admin/CommercialTerms.js'), 'CommercialTermsPage');
const CommercialTermsConfirmPage = lazyNamed(() => import('./pages/admin/CommercialTermsConfirm.js'), 'CommercialTermsConfirmPage');
const CommercialTermsDemoPage = lazyNamed(() => import('./pages/admin/CommercialTermsDemo.js'), 'CommercialTermsDemoPage');
const UsersPage = lazyNamed(() => import('./pages/admin/Users.js'), 'UsersPage');
const SalesPage = lazyNamed(() => import('./pages/admin/Sales.js'), 'SalesPage');
const ShippedOrdersPage = lazyNamed(() => import('./pages/admin/ShippedOrders.js'), 'ShippedOrdersPage');
const PlanFactPage = lazyNamed(() => import('./pages/admin/PlanFact.js'), 'PlanFactPage');
const AiAssistantPage = lazyNamed(() => import('./pages/ai/AiAssistant.js'), 'AiAssistantPage');
const Card41Page = lazyNamed(() => import('./pages/admin/Card41.js'), 'Card41Page');
// 28.10.2026: отдельная страница сверки скрыта — сверка стала вкладкой карточки 41; старый адрес ведёт туда.
// const ReconcilePage = lazyNamed(() => import('./pages/admin/Reconcile.js'), 'ReconcilePage');
const PlanFactDashboardPage = lazyNamed(() => import('./pages/admin/PlanFactDashboard.js'), 'PlanFactDashboardPage');
const PnLPage = lazyNamed(() => import('./pages/admin/PnL.js'), 'PnLPage');
const PnLDashboardPage = lazyNamed(() => import('./pages/admin/PnLDashboard.js'), 'PnLDashboardPage');
const SuppliersPage = lazyNamed(() => import('./pages/admin/Suppliers.js'), 'SuppliersPage');
const OrdersListPage = lazyNamed(() => import('./pages/orders/List.js'), 'OrdersListPage');
const OrderEditPage = lazyNamed(() => import('./pages/orders/Edit.js'), 'OrderEditPage');
const OrderDetailPage = lazyNamed(() => import('./pages/orders/Detail.js'), 'OrderDetailPage');
const ClientOrdersListPage = lazyNamed(() => import('./pages/client/OrdersList.js'), 'ClientOrdersListPage');
const ClientOrderDetailPage = lazyNamed(() => import('./pages/client/OrderDetail.js'), 'ClientOrderDetailPage');
const ClientCatalogPage = lazyNamed(() => import('./pages/client/Catalog.js'), 'ClientCatalogPage');
const ClientFinancePage = lazyNamed(() => import('./pages/client/Finance.js'), 'ClientFinancePage');
const SalesAnalyticsPage = lazyNamed(() => import('./pages/client/SalesAnalytics.js'), 'SalesAnalyticsPage');
const AccountsReceivablePage = lazyNamed(() => import('./pages/AccountsReceivable.js'), 'AccountsReceivablePage');
const StockPage = lazyNamed(() => import('./pages/Stock.js'), 'StockPage');
const ClientProfilePage = lazyNamed(() => import('./pages/clients/ClientProfile.js'), 'ClientProfilePage');
const ClientsListPage = lazyNamed(() => import('./pages/clients/ClientsList.js'), 'ClientsListPage');
const InviteClientPage = lazyNamed(() => import('./pages/clients/InviteClient.js'), 'InviteClientPage');
const NewsPage = lazyNamed(() => import('./pages/News.js'), 'NewsPage');
const CounterpartiesPage = lazyNamed(() => import('./pages/Counterparties.js'), 'CounterpartiesPage');
const CounterpartyNewPage = lazyNamed(() => import('./pages/CounterpartyNew.js'), 'CounterpartyNewPage');
const CounterpartyDetailPage = lazyNamed(() => import('./pages/CounterpartyDetail.js'), 'CounterpartyDetailPage');
const PendingCounterpartiesPage = lazyNamed(() => import('./pages/admin/PendingCounterparties.js'), 'PendingCounterpartiesPage');

const SIMPLE_KINDS: SimpleCatalogKind[] = ['carrier', 'vehicle', 'driver', 'extra_service', 'order_type', 'warehouse'];

function Protected({
  children, forbidClient, clientOnly, planFactOnly, pnlOnly,
}: {
  children: ReactNode;
  forbidClient?: boolean;
  clientOnly?: boolean;
  /** Ограничивает страницу «План-Факт» ролям из canSeePlanFact (sections.ts). */
  planFactOnly?: boolean;
  /** Ограничивает страницу «P&L» ролям из canSeePnl (sections.ts). */
  pnlOnly?: boolean;
}) {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return <Center h="100vh"><Loader /></Center>;
  }
  if (state.status === 'anon') return <Navigate to="/login" replace />;
  const isClient = isClientUser(state.user);
  // Клиенту закрыты служебные разделы — редирект на его кабинет.
  if (forbidClient && isClient) {
    return <Navigate to="/client/orders" replace />;
  }
  // «План-Факт»: та же проверка, что и в sidebar (canSeePlanFact — единый
  // источник прав видимости, sections.ts). Сервер всё равно проверяет
  // requireRole('admin', 'senior_manager').
  if (planFactOnly && (isClient || !canSeePlanFact(state.user.role))) {
    return <Navigate to="/" replace />;
  }
  // «P&L»: уже, чем План-Факт (без senior_manager), условие берётся из canSeePnl —
  // единого источника прав видимости (sections.ts), чтобы меню и маршрут
  // нельзя было развести. Сервер проверяет роль независимо.
  if (pnlOnly && (isClient || !canSeePnl(state.user.role))) {
    return <Navigate to="/" replace />;
  }
  // Сотрудникам закрыта клиентская проекция — редирект на общий список.
  if (clientOnly && !isClient) {
    return <Navigate to="/orders" replace />;
  }
  // У каждой стороны свой каркас: клиентский кабинет и внутренний портал.
  // До редизайна у сотрудников оболочки не было — только полоска BackHome+NowBar.
  if (isClient) {
    return <ClientShell>{children}</ClientShell>;
  }
  return <InternalShell>{children}</InternalShell>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return <Center h="100vh"><Loader /></Center>;
  }
  if (state.status === 'authed') return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Клиент попадает на /orders/* (по старым ссылкам/закладкам) — редиректим в свой кабинет.
function OrdersListRouter() {
  const { state } = useAuth();
  if (state.status === 'authed') {
    const isClient = isClientUser(state.user);
    if (isClient) return <Navigate to="/client/orders" replace />;
  }
  return <OrdersListPage />;
}
function OrderDetailRouter() {
  const { id } = useParams<{ id: string }>();
  const { state } = useAuth();
  if (state.status === 'authed') {
    const isClient = isClientUser(state.user);
    if (isClient) return <Navigate to={`/client/orders/${id ?? ''}`} replace />;
  }
  return <OrderDetailPage />;
}
function OrderEditRouter() {
  const { id } = useParams<{ id: string }>();
  const { state } = useAuth();
  if (state.status === 'authed') {
    const isClient = isClientUser(state.user);
    if (isClient) return <Navigate to={`/client/orders/${id ?? ''}`} replace />;
  }
  return <OrderEditPage />;
}

function SimpleCatalogRoute() {
  const { kind } = useParams<{ kind: string }>();
  if (!kind || !SIMPLE_KINDS.includes(kind as SimpleCatalogKind)) {
    return <Navigate to="/" replace />;
  }
  return <SimpleCatalogPage kind={kind as SimpleCatalogKind} />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RootErrorBoundary>
        <Suspense fallback={<Center h="100vh"><Loader /></Center>}>
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/reset-password" element={<PublicOnly><ResetRequestPage /></PublicOnly>} />
          <Route path="/reset-password/confirm" element={<ResetConfirmPage />} />
          <Route path="/" element={<Protected><HomePage /></Protected>} />
          <Route path="/news" element={<Protected><NewsPage /></Protected>} />
          <Route path="/profile" element={<Protected><ClientProfilePage /></Protected>} />
          <Route path="/admin/producers" element={<Protected forbidClient><ProducersPage /></Protected>} />
          <Route path="/admin/product-groups" element={<Protected forbidClient><ProductGroupsPage /></Protected>} />
          <Route path="/admin/products" element={<Protected forbidClient><ProductsPage /></Protected>} />
          <Route path="/admin/legal-entities" element={<Protected forbidClient><LegalEntitiesPage /></Protected>} />
          <Route path="/admin/suppliers" element={<Protected forbidClient><SuppliersPage /></Protected>} />
          <Route path="/admin/users" element={<Protected forbidClient><UsersPage /></Protected>} />
          <Route path="/admin/catalogs/:kind" element={<Protected forbidClient><SimpleCatalogRoute /></Protected>} />
          <Route path="/admin/commercial-terms" element={<Protected forbidClient><CommercialTermsPage /></Protected>} />
          {/* Демо-макет на выдуманных данных, ссылок в интерфейсе нет. До
              07.08.2026 маршрут был БЕЗ гарда и открывался анонимно — наружу
              торчал внутренний экран (утечки данных не было, но структура КУ
              видна). Закрыт наравне с остальными служебными разделами. */}
          <Route path="/admin/commercial-terms-demo" element={<Protected forbidClient><CommercialTermsDemoPage /></Protected>} />
          <Route path="/admin/commercial-terms/:id/confirm" element={<Protected forbidClient><CommercialTermsConfirmPage /></Protected>} />
          <Route path="/admin/pending-counterparties" element={<Protected forbidClient><PendingCounterpartiesPage /></Protected>} />
          <Route path="/orders" element={<Protected><OrdersListRouter /></Protected>} />
          <Route path="/orders/:id" element={<Protected><OrderDetailRouter /></Protected>} />
          <Route path="/orders/:id/edit" element={<Protected><OrderEditRouter /></Protected>} />
          <Route path="/client/orders" element={<Protected clientOnly><ClientOrdersListPage /></Protected>} />
          <Route path="/client/orders/:id" element={<Protected clientOnly><ClientOrderDetailPage /></Protected>} />
          <Route path="/client/orders/:id/catalog" element={<Protected clientOnly><ClientCatalogPage /></Protected>} />
          <Route path="/client/finance" element={<Protected clientOnly><ClientFinancePage /></Protected>} />
          <Route path="/client/sales" element={<Protected clientOnly><SalesAnalyticsPage /></Protected>} />
          <Route path="/ar" element={<Protected forbidClient><AccountsReceivablePage /></Protected>} />
          <Route path="/stock" element={<Protected forbidClient><StockPage /></Protected>} />
          <Route path="/counterparties" element={<Protected forbidClient><CounterpartiesPage /></Protected>} />
          <Route path="/counterparties/new" element={<Protected forbidClient><CounterpartyNewPage /></Protected>} />
          <Route path="/counterparties/:id" element={<Protected forbidClient><CounterpartyDetailPage /></Protected>} />
          <Route path="/clients" element={<Protected forbidClient><ClientsListPage /></Protected>} />
          <Route path="/clients/invite" element={<Protected forbidClient><InviteClientPage /></Protected>} />
          <Route path="/admin/sales" element={<Protected forbidClient><SalesPage /></Protected>} />
          <Route path="/admin/shipped" element={<Protected forbidClient><ShippedOrdersPage /></Protected>} />
          {/* План-Факт: дашборд — основной вид; классический «заархивирован» на /classic
              (решение Artem 08.09.2026). Старый адрес /dashboard редиректит, чтобы ссылки не ломались. */}
          <Route path="/admin/plan-fact" element={<Protected forbidClient planFactOnly><PlanFactDashboardPage /></Protected>} />
          <Route path="/admin/plan-fact/classic" element={<Protected forbidClient planFactOnly><PlanFactPage /></Protected>} />
          <Route path="/admin/plan-fact/dashboard" element={<Navigate to="/admin/plan-fact" replace />} />
          {/* P&L устроен как План-Факт: дашборд — основной вид, классическая
              помесячная таблица «заархивирована» на /classic. Временный адрес
              /admin/pnl/dashboard редиректит, чтобы уже разосланные ссылки не ломались. */}
          <Route path="/admin/pnl" element={<Protected forbidClient pnlOnly><PnLDashboardPage /></Protected>} />
          {/* ИИ аналитик (09.10.2026): доступ по списку на сервере, страница сама показывает «Нет доступа». */}
          <Route path="/ai" element={<Protected forbidClient><AiAssistantPage /></Protected>} />
          {/* Карточка 41: доступ решает сервер (admin или CARD41_USERS), страница сама покажет «нет доступа». */}
          <Route path="/admin/card41" element={<Protected forbidClient><Card41Page /></Protected>} />
          {/* <Route path="/admin/reconcile" element={<Protected forbidClient><ReconcilePage /></Protected>} /> */}
          <Route path="/admin/reconcile" element={<Navigate to="/admin/card41?tab=reconcile" replace />} />
          <Route path="/admin/pnl/classic" element={<Protected forbidClient pnlOnly><PnLPage /></Protected>} />
          <Route path="/admin/pnl/dashboard" element={<Navigate to="/admin/pnl" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </RootErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
